// src/lib/planGenerator.js

import level2Template from "../plans/5k/data/5kTrainingPlan.json"; // default template Level 2
import level1Template from "../plans/5k/data/5kTrainingPlan1.json"; // Level 1 Template (easier)
import level3Template from "../plans/5k/data/5kTrainingPlan2.json"; // Level 3 Template (harder)

/**
 * Generates a populated training plan by taking the appropriate JSON template (based on plan level),
 * splitting it into two blocks (first 3 workouts and last 2), and then reordering block2 based on the user's ambiguous selections.
 *
 * @param {import("./paceCalculator").PaceResults} paces - The computed pace values.
 * @param {{ id: string, block1: string[], block2: string[], ambiguousDays: string[] }} validOption - The valid option chosen by the user (defines day blocks).
 * @param {{ [day: string]: "Easy Run #2" | "Long Run" }} ambiguousSelections - Mapping of ambiguous day names to chosen workout type.
 * @param {number} planLevel - The selected plan level (1, 2, or 3) to choose the JSON template.
 * @returns {Record<string, Array<{ day: string, workout: any }>>} The training plan with workouts assigned to specific days.
 */
export function generateTrainingPlan(paces, validOption, ambiguousSelections, planLevel) {
    // Choose the appropriate template.
    const templates = {
        1: level1Template,
        2: level2Template,
        3: level3Template,
    };

    const chosenTemplate = templates[planLevel];
    if (!chosenTemplate) {
        throw new Error(`Template for plan level ${planLevel} is not defined.`);
    }

    // Deep copy the chosen JSON template.
    const rawTemplate = JSON.parse(JSON.stringify(chosenTemplate));

    const generatedPlan = {};

    // Helper to replace pace placeholders in a workout.
    const processWorkout = (workout) => {
        const updated = { ...workout };

        const replacePaceTokens = (text) =>
            text
                .replace(/{Easy Run Pace}/g, paces.easyRun.high)
                .replace(/{Threshold Pace}/g, paces.threshold)
                .replace(/{Long Run Pace}/g, paces.longRun || "")
                .replace(/{Test Pace \(vVO2max\)}/g, paces.vVO2max || "");

        if (updated.Pace && typeof updated.Pace === "string") {
            updated.Pace = replacePaceTokens(updated.Pace);
        }

        if (updated.Description && typeof updated.Description === "string") {
            updated.Description = replacePaceTokens(updated.Description);
        }

        return updated;
    };

    // Process each week in the template.
    for (const weekKey in rawTemplate) {
        if (!Object.prototype.hasOwnProperty.call(rawTemplate, weekKey)) continue;

        const weekWorkouts = rawTemplate[weekKey].map(processWorkout);

        // Expect exactly 5 workouts per week.
        const block1Workouts = weekWorkouts.slice(0, 3);
        let block2Workouts = weekWorkouts.slice(3, 5); // default: [Easy Run #2, Long Run]

        // If the ambiguous selection for the first block2 day is "Long Run", swap the two workouts.
        if (ambiguousSelections?.[validOption.block2[0]] === "Long Run") {
            block2Workouts = [block2Workouts[1], block2Workouts[0]];
        }

        // Assign workouts to days.
        const dayWorkouts = [];

        validOption.block1.forEach((day, i) => {
            dayWorkouts.push({ day, workout: block1Workouts[i] });
        });

        validOption.block2.forEach((day, i) => {
            dayWorkouts.push({ day, workout: block2Workouts[i] });
        });

        generatedPlan[weekKey] = dayWorkouts;
    }

    return generatedPlan;
}
